import React from 'react';
import styled from 'styled-components';
import { Ticket, Package } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { formatInt } from './participantTokens';
import { panelAccent } from 'theme/panelAccent';

/**
 * Progresso de check-in do evento.
 *
 * Fica no topo e **sempre visível**: antes ele vinha depois dos filtros e
 * desaparecia quando a busca não retornava nada, mas esse número descreve o
 * evento, não o resultado do filtro.
 *
 * Mostra "faltam entrar" além de "entraram" — é o número que a portaria usa,
 * e a versão anterior só exibia o percentual já feito.
 */
export default function CheckinProgress({ progress, dayLabel = null }) {
    const tickets = progress?.tickets;
    const products = progress?.products;

    const ticketTotal = Number(tickets?.total_tickets) || 0;
    const ticketDone = Number(tickets?.ticket_checkin) || 0;
    const productTotal = Number(products?.total_products) || 0;
    const productDone = Number(products?.product_checkin) || 0;

    if (ticketTotal === 0 && productTotal === 0) return null;

    return (
        <Wrapper>
            {ticketTotal > 0 && (
                <Block
                    icon={Ticket}
                    label={dayLabel ? `Check-in de ingressos · ${dayLabel}` : 'Check-in de ingressos'}
                    done={ticketDone}
                    total={ticketTotal}
                    doneWord='entraram'
                    pendingWord='faltam entrar'
                />
            )}

            {ticketTotal > 0 && productTotal > 0 && <Divider aria-hidden='true' />}

            {productTotal > 0 && (
                <Block
                    icon={Package}
                    label='Produtos validados'
                    done={productDone}
                    total={productTotal}
                    doneWord='validados'
                    pendingWord='a validar'
                />
            )}
        </Wrapper>
    );
}

function Block({ icon: Icon, label, done, total, doneWord, pendingWord }) {
    const pending = Math.max(total - done, 0);
    const percent = total > 0 ? (done / total) * 100 : 0;
    const rounded = percent > 0 && percent < 1 ? 1 : Math.round(percent);

    return (
        <BlockRoot>
            <Head>
                <Label>
                    <Icon size={13} strokeWidth={2.5} aria-hidden='true' />
                    {label}
                </Label>
                <Percent>{rounded}%</Percent>
            </Head>

            <Numbers>
                <Primary>{formatInt(done)}</Primary>
                <PrimaryWord>{doneWord}</PrimaryWord>
                <Sep aria-hidden='true'>·</Sep>
                <Secondary>{formatInt(pending)}</Secondary>
                <SecondaryWord>{pendingWord}</SecondaryWord>
                <Total>de {formatInt(total)}</Total>
            </Numbers>

            <Track
                role='progressbar'
                aria-valuenow={rounded}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${label}: ${done} de ${total}`}
            >
                <Fill $percent={Math.min(Math.max(percent, 0), 100)} />
            </Track>
        </BlockRoot>
    );
}

const Wrapper = styled.section`
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding: 18px 20px;
    border-radius: 16px;
    background: ${colors.neutralLight};
    border: 1px solid rgba(16, 24, 40, 0.1);
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);

    @media screen and (min-width: ${breakpoints.smd}) {
        flex-direction: row;
        gap: 0;
    }
`;

const BlockRoot = styled.div`
    flex: 1;
    min-width: 0;
`;

const Divider = styled.div`
    width: 1px;
    background: rgba(16, 24, 40, 0.12);
    margin: 0 24px;
    flex-shrink: 0;
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 6px;
`;

const Label = styled.p`
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.7px;
    text-transform: uppercase;
    color: ${colors.night};
`;

const Percent = styled.span`
    font-size: 14px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: ${colors.nightDark};
`;

const Numbers = styled.p`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 5px;
    margin: 0 0 10px;
`;

const Primary = styled.span`
    font-size: 28px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -1px;
    color: ${colors.nightDark};
`;

const PrimaryWord = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const Sep = styled.span`
    color: ${colors.nightLight};
`;

const Secondary = styled.span`
    font-size: 18px;
    font-weight: 700;
    letter-spacing: -0.4px;
    color: ${colors.night};
`;

const SecondaryWord = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const Total = styled.span`
    margin-left: auto;
    font-size: 12px;
    color: ${colors.night};
`;

const Track = styled.div`
    width: 100%;
    height: 10px;
    border-radius: 999px;
    background: #ebdcfb;
    overflow: hidden;
`;

const Fill = styled.div`
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    min-width: ${({ $percent }) => ($percent > 0 ? '4px' : '0')};
    border-radius: 999px;
    background: ${panelAccent(colors.secondaryPurple)};
    transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
`;
